import { DialogRef } from '@angular/cdk/dialog';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import {
  MibLogoMedinboxComponent,
  MibModalComponent,
  MibScrollFadeDirective,
} from '@medinbox/angular';
import { MarkdownComponent } from 'ngx-markdown';

interface Tab {
  title: string;
  file: string;
}

@Component({
  selector: 'mai-terms-conditions',
  imports: [MibModalComponent, MibLogoMedinboxComponent, MibScrollFadeDirective, MarkdownComponent],
  templateUrl: './terms-conditions.dialog.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TermsConditionsDialog {
  readonly dialogRef = inject(DialogRef);

  readonly tabs = [
    {
      title: 'Terms of service',
      file: 'terms-conditions/Terms_of_Service.md',
    },
    {
      title: 'License Agreement',
      file: 'terms-conditions/Global_Software_License.md',
    },
    {
      title: 'Business Associate & Data Processing Agreement',
      file: 'terms-conditions/Global_BAA_Medinbox.md',
    },
  ];
  readonly currentTab = signal(this.tabs[0].file);
  readonly tabContent = viewChild.required<ElementRef<HTMLDivElement>>('tabContent');
  private readonly markdown = viewChild.required(MarkdownComponent, { read: ElementRef });

  /* The section titles stick below the global title, whose height changes as it wraps. */
  private readonly titleObserver =
    typeof ResizeObserver === 'undefined'
      ? undefined
      : new ResizeObserver(([entry]) =>
          this.markdown().nativeElement.style.setProperty(
            '--terms-title-height',
            `${entry.borderBoxSize[0].blockSize}px`,
          ),
        );

  constructor() {
    inject(DestroyRef).onDestroy(() => this.titleObserver?.disconnect());
  }

  onError(event: unknown): void {
    console.error('Error while loading terms and conditions:', event);
  }

  /* A sticky title leaves with its parent: each `h2` and its text go in a `section`. */
  protected onReady(): void {
    const root: HTMLElement = this.markdown().nativeElement;
    const title = root.querySelector('h1');

    for (const heading of Array.from(root.querySelectorAll('h2'))) {
      const section = document.createElement('section');
      heading.before(section);
      section.append(heading);
      while (section.nextSibling && section.nextSibling.nodeName !== 'H2') {
        section.append(section.nextSibling);
      }
    }

    this.titleObserver?.disconnect();
    if (title) {
      this.titleObserver?.observe(title);
    }
  }

  selectTab(tab: Tab): void {
    this.currentTab.set(tab.file);
    this.tabContent().nativeElement.scrollTo({ top: 0 });
  }
}
